import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Badge, Spin } from 'antd';
import { Button, Popover } from '@bettercomp/ui-components';
import { selectGroupedNotifications } from 'redux/notifications/selectors';
import { getJobTitleByType } from 'redux/notifications';
import { formatDate, YEAR_DATE_TIME_WITH_SECONDS_FORMAT } from 'utils/date';
import { AsyncJobType } from 'types';
import { BellOutlined, DownOutlined, FolderAddOutlined, UpOutlined } from '@ant-design/icons';
import cn from './styles.module.scss';

export const Notifications = () => {
  const [expanded, setExpanded] = useState<AsyncJobType[]>([]);
  const notificationGroups = useSelector(selectGroupedNotifications);
  const notificationsCount = notificationGroups.reduce((sum, notificationGroup) => {
    return sum + notificationGroup.notifications.length;
  }, 0);

  const handleGroupClick = (type: AsyncJobType) => () => {
    if (expanded.includes(type)) {
      setExpanded(expanded.filter(expandedItem => expandedItem !== type));
    } else {
      setExpanded([...expanded, type]);
    }
  };

  return (
    <Popover
      classNames={{ root: cn.popoverRoot, container: cn.popoverContainer }}
      content={() => (
        <div className={cn.notifications}>
          {notificationsCount === 0 && (
            <div className={cn.noData}>
              <span>There are no notifications yet</span>
              <FolderAddOutlined />
            </div>
          )}
          {notificationGroups.map(notificationGroup => (
            <div className={cn.notificationGroups} key={notificationGroup.type}>
              <div className={cn.groupTitle}>
                {getJobTitleByType(notificationGroup.type)}
                <div className={cn.actions}>
                  {notificationGroup.inProgress && (
                    <Spin size="small" />
                  )}
                  <Badge count={notificationGroup.notifications.length} />
                  {expanded.includes(notificationGroup.type)
                    ? <Button size="small" type="text" onClick={handleGroupClick(notificationGroup.type)} icon={<UpOutlined />} />
                    : <Button size="small" type="text" onClick={handleGroupClick(notificationGroup.type)} icon={<DownOutlined />} />
                  }
                </div>
              </div>
              {expanded.includes(notificationGroup.type) && notificationGroup.notifications.map(notification => (
                <div key={notification.id} className={cn.notification}>
                  <div className={cn.info}>
                    <span className={cn.title}>{getJobTitleByType(notification.type)}</span>
                    <span className={cn.date}>{formatDate(notification.createdAt, YEAR_DATE_TIME_WITH_SECONDS_FORMAT)}</span>
                  </div>
                  <div className={cn.status}>
                    {notification.status === 'inProgress' && (
                      <Spin size="small" />
                    )}
                    <div>{notification.status}</div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      trigger="click"
      placement="bottomRight"
      onOpenChange={(isVisible) => !isVisible && setExpanded([])}
    >
      <Badge count={notificationsCount} data-testid="notificationsCount" data-count={notificationsCount}>
        <Button
          type="text"
          icon={<BellOutlined className={cn.notificationIcon} />}
          style={{ background: "transparent" }}
        />
      </Badge>
    </Popover>
  );
};
